import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CaretLeft, ChartBar } from '@phosphor-icons/react';
import { useAuth } from '../../../App';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { getEconomicsProductDetail } from '../api';
import { money, StatCard, Panel, MarginCell, EmptyHint, SuperAdminGate, EconomicsSourceNote, LineMoney, LineBilling } from './economicsShared';
import { fmtLabel } from '../ui';

function EconomicsProductDetailPage() {
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('clients');

  useEffect(() => {
    if (user?.role !== 'super_admin' || !productId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getEconomicsProductDetail(productId)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user?.role, productId]);

  const product = data?.product || {};
  const currency = data?.currency || 'INR';
  const clients = data?.clients || [];
  const people = data?.people || [];
  const infrastructure = data?.infrastructure || [];
  const devices = data?.devices || [];

  const tabs = [
    { id: 'clients', label: `Clients (${clients.length})` },
    { id: 'people', label: `People (${people.length})` },
    { id: 'infra', label: `Infrastructure (${infrastructure.length})` },
    { id: 'devices', label: `Devices (${devices.length})` },
  ];

  return (
    <div data-testid="economics-product-detail-page">
      <SuperAdminGate user={user}>
        <div style={{ marginBottom: '12px' }}>
          <Link
            to="/dashboard/assets/economics/products"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6B7280', textDecoration: 'none' }}
          >
            <CaretLeft size={14} /> All products
          </Link>
          <h2 style={{ margin: '6px 0 2px', fontSize: '16px', fontWeight: 700 }}>
            {product.product_name || productId}
          </h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
            Full revenue and allocated spend for every client under this product.
          </p>
          <EconomicsSourceNote />
        </div>

        {loading ? <SkeletonBlock rows={8} /> : error ? (
          <EmptyState icon={ChartBar} title="Could not load" description={String(error)} />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '14px' }}>
              <StatCard label="Clients" value={product.client_count || 0} color="#0EA5E9" />
              <StatCard label="Revenue / mo" value={money(product.revenue_monthly, currency)} color="#059669" />
              <StatCard label="Spend / mo" value={money(product.spend_monthly, currency)} color="#DC2626" />
              <StatCard label="People / mo" value={money(product.spend_people_monthly, currency)} color="#7C3AED" />
              <StatCard label="Infra / mo" value={money(product.spend_infra_monthly, currency)} color="#D97706" />
              <StatCard label="Devices / mo" value={money(product.spend_device_monthly, currency)} color="#6366F1" />
              <StatCard label="Margin" value={money(product.margin_monthly, currency)} sub={`${product.margin_pct || 0}%`} color={(product.margin_monthly || 0) >= 0 ? '#002FA7' : '#B91C1C'} />
              <StatCard label="Purchase allocated" value={money(product.spend_purchase, currency)} color="#9CA3AF" />
            </div>

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={tab === t.id ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'clients' && (
              <Panel title="Clients under this product">
                {clients.length === 0 ? <EmptyHint>No clients on this product.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Billing</th>
                          <th>Revenue / mo</th>
                          <th>Spend / mo</th>
                          <th>People</th>
                          <th>Infra</th>
                          <th>Devices</th>
                          <th>Margin</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.map((c) => (
                          <tr key={c.client_id}>
                            <td style={{ fontWeight: 600 }}>{c.client_name}</td>
                            <td style={{ fontSize: '12px', color: '#6B7280' }}>
                              <LineBilling amount={c.billing_amount} period={c.billing_period} currency={c.currency || currency} amcAmount={c.amc_amount} />
                            </td>
                            <td><LineMoney value={c.revenue_monthly} currency={currency} /></td>
                            <td><LineMoney value={c.spend_monthly} currency={currency} /></td>
                            <td>{c.member_count}</td>
                            <td>{c.infra_count}</td>
                            <td>{c.device_count}</td>
                            <td><MarginCell value={c.margin_monthly} pct={c.margin_pct} /></td>
                            <td>
                              <button
                                type="button"
                                className="btn-secondary"
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                onClick={() => navigate(`/dashboard/assets/economics/clients/${c.client_id}`)}
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {tab === 'people' && (
              <Panel title="People cost (Payroll CTC) allocated to this product’s clients">
                {people.length === 0 ? <EmptyHint>No members with allocated cost.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Role</th>
                          <th>Clients served</th>
                          <th>Monthly CTC</th>
                          <th>Allocated share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p, i) => (
                          <tr key={`${p.user_id}-${i}`}>
                            <td style={{ fontWeight: 600 }}>{p.name}</td>
                            <td>{p.org_role || p.designation || '—'}</td>
                            <td>{p.client_count}</td>
                            <td><LineMoney value={p.monthly_cost} currency={currency} /></td>
                            <td><LineMoney value={p.shared_monthly_cost} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {tab === 'infra' && (
              <Panel title="Infrastructure (direct to client)">
                {infrastructure.length === 0 ? <EmptyHint>No infrastructure costs.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Application / asset</th>
                          <th>Client</th>
                          <th>Environment</th>
                          <th>Hosting</th>
                          <th>Purchase</th>
                          <th>Monthly</th>
                        </tr>
                      </thead>
                      <tbody>
                        {infrastructure.map((row) => (
                          <tr key={row.asset_id}>
                            <td style={{ fontWeight: 600 }}>{row.application_name || row.name}</td>
                            <td>{row.client_name}</td>
                            <td>{row.environment || '—'}</td>
                            <td>{row.hosting_location || '—'}</td>
                            <td><LineMoney value={row.cost} currency={currency} /></td>
                            <td><LineMoney value={row.monthly_cost} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {tab === 'devices' && (
              <Panel title="Devices allocated via team members">
                {devices.length === 0 ? <EmptyHint>No device costs allocated.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Device</th>
                          <th>Type</th>
                          <th>Assignee</th>
                          <th>Client</th>
                          <th>Share</th>
                          <th>Purchase alloc.</th>
                          <th>Monthly alloc.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((row) => (
                          <tr key={`${row.asset_id}-${row.client_id}`}>
                            <td style={{ fontWeight: 600 }}>{row.name}</td>
                            <td style={{ textTransform: 'capitalize' }}>{fmtLabel(row.asset_type)}</td>
                            <td>{row.assigned_to_name || '—'}</td>
                            <td>{row.client_name}</td>
                            <td>{row.share === 'shared' ? `1/${row.client_count}` : 'pinned'}</td>
                            <td><LineMoney value={row.allocated_purchase} currency={currency} /></td>
                            <td><LineMoney value={row.allocated_monthly} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}
          </>
        )}
      </SuperAdminGate>
    </div>
  );
}

export default EconomicsProductDetailPage;
